import { useState } from "react";
import { Box, Button, Typography, Chip, Paper, Link } from "@mui/material";
import { useNavigate } from "react-router-dom";
import RoleList from "./RoleList";
import RolePermissionsMatrix from "./RolePermissionsMatrix";
import CreateEditRoleDialog from "./CreateEditRoleDialog";

const RolesPermissionsTab = () => {
  const [selectedRole, setSelectedRole] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [saveTrigger, setSaveTrigger] = useState(0);
  const navigate = useNavigate();

  const isSystem =
    selectedRole?.roleType === "SYSTEM" || selectedRole?.isSystem === true;

  return (
    <Box sx={{ display: "flex", height: "calc(100vh - 160px)", gap: 0 }}>
      {/* Left panel — role list */}
      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          border: "1px solid #E4E3E3",
          borderRadius: "8px 0 0 8px",
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #E4E3E3",
          }}
        >
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#2E2E2E" }}>
            Roles
          </Typography>
          <Button
            variant="contained"
            onClick={() => setCreateDialogOpen(true)}
          >
            + Create
          </Button>
        </Box>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <RoleList
            refreshKey={refreshKey}
            selectedRole={selectedRole}
            onSelectRole={setSelectedRole}
            onRolesChanged={() => setRefreshKey((k) => k + 1)}
          />
        </Box>
      </Box>

      {/* Right panel — role detail */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          border: "1px solid #E4E3E3",
          borderLeft: "none",
          borderRadius: "0 8px 8px 0",
          backgroundColor: "#fff",
        }}
      >
        {!selectedRole ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Typography color="text.secondary">
              Select a role to view and edit its permissions
            </Typography>
          </Box>
        ) : (
          <>
            {/* Role header */}
            <Box
              sx={{
                px: 3,
                py: 2,
                borderBottom: "1px solid #E4E3E3",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
              }}
            >
              <Box>
                <Typography sx={{ fontSize: 12, color: "#888", mb: 0.5 }}>
                  Roles & Permissions
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{ fontSize: 22, fontWeight: 700, color: "#2E2E2E" }}
                  >
                    {selectedRole.roleName}
                  </Typography>
                  <Chip
                    label={isSystem ? "System" : "Custom"}
                    size="small"
                    sx={{
                      backgroundColor: isSystem ? "#EEEFFF" : "#F0FFF4",
                      color: isSystem ? "#4D44D8" : "#166534",
                      fontWeight: 600,
                      fontSize: 11,
                    }}
                  />
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5, mt: 0.5 }}>
                <Button
                  variant="contained"
                  onClick={() => setSaveTrigger((n) => n + 1)}
                >
                  Save changes
                </Button>
              </Box>
            </Box>

            {/* Info cards */}
            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                gap: 2,
                borderBottom: "1px solid #E4E3E3",
              }}
            >
              {[
                {
                  label: "Role Code",
                  value: selectedRole.roleCode || "—",
                  mono: true,
                },
                { label: "Type", value: isSystem ? "System" : "Custom" },
                {
                  label: "Assigned Users",
                  value: `${selectedRole.userCount ?? 0} users`,
                  // Deep-link to Manage Users with this role preselected in the filter.
                  link: selectedRole.roleCode
                    ? {
                        text: "View Users",
                        to: `/access-management/users?roleCodes=${encodeURIComponent(
                          selectedRole.roleCode
                        )}`,
                      }
                    : null,
                },
              ].map(({ label, value, mono, link }) => (
                <Paper
                  key={label}
                  variant="outlined"
                  sx={{ px: 2.5, py: 2, flex: 1, borderRadius: 2 }}
                >
                  <Typography sx={{ fontSize: 13, color: "#888", mb: 0.75 }}>
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: "#2E2E2E",
                        fontFamily: mono ? "monospace" : undefined,
                      }}
                    >
                      {value}
                    </Typography>
                    {link && (
                      <Link
                        component="button"
                        type="button"
                        onClick={() => navigate(link.to)}
                        data-testid="view-users-link"
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#4D44D8",
                          textDecoration: "none",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        {link.text}
                      </Link>
                    )}
                  </Box>
                </Paper>
              ))}
            </Box>

            {/* Permissions matrix */}
            <Box sx={{ px: 3, py: 2 }}>
              <RolePermissionsMatrix
                roleId={selectedRole.id}
                roleName={selectedRole.roleName}
                saveTrigger={saveTrigger}
              />
            </Box>
          </>
        )}
      </Box>

      <CreateEditRoleDialog
        open={createDialogOpen}
        mode="create"
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setRefreshKey((k) => k + 1);
          setCreateDialogOpen(false);
        }}
      />
    </Box>
  );
};

export default RolesPermissionsTab;
